import { useEffect, useState } from 'react';

/**
 * Three-tier subscription model. Free is the lead magnet; Hunter is the
 * everyday bowhunter's tier; Pro is the everything-on power-user tier.
 *
 * Ranks (used for tier comparisons): free=0 < hunter=1 < pro=2.
 */
export type Tier = 'free' | 'hunter' | 'pro';

export const TIER_RANK: Record<Tier, number> = { free: 0, hunter: 1, pro: 2 };

export interface Subscription {
  tier: Tier;
  /** Convenience flag — true only at the top tier. */
  isPro: boolean;
  /** True for hunter OR pro. Used to unlock mid-tier features. */
  isHunterOrAbove: boolean;
  /** Returns true if user's tier is ≥ required. Use for feature gates. */
  hasTier: (required: Tier) => boolean;
}

/**
 * Feature ceiling per tier:
 *
 *   FREE ($0 — lead magnet, captures email, drives Firenock kit sales)
 *     - Basic builder: pick shaft / spine / point weight
 *     - Live FOC + total weight + KE readout
 *     - 1 saved build at a time
 *     - Add-to-cart for titanium kits and any single component
 *
 *   HUNTER ($4/mo or $36/yr — everyday bowhunter)
 *     - Everything in Free, plus:
 *     - Recommender (goal → top 5 builds)
 *     - Full component picker (insert / nock / vane choices)
 *     - Sight tape generation + PDF export (single yardage range)
 *     - 5 saved builds
 *     - Email PDF / share build links
 *
 *   PRO ($9/mo or $79/yr — competition + western hunter + Africa class)
 *     - Everything in Hunter, plus:
 *     - Multi-setup (unlimited bows)
 *     - Shot Solver (atmospheric DA, wind drift, hold solution)
 *     - Dual-chrono calibration
 *     - Advanced fields (IBO cam tuning, let-off, vane back-position, etc.)
 *     - Travel Tapes (atmospheric corrections for hunt destination)
 *     - 10% discount on storefront orders
 *
 * The UI complexity toggle (Simple / Standard / Expert) is orthogonal —
 * available on every tier and only affects which fields are visible.
 *
 * In a future iteration, this hook will hit /api/entitlement (see DEPLOY 3,
 * task #89) and validate via signed JWT instead of reading window globals.
 */
export function useSubscription(): Subscription {
  const [tier, setTier] = useState<Tier>(() => readEffectiveTier());

  useEffect(() => {
    const onTierChange = () => setTier(readEffectiveTier());
    window.addEventListener('sparrow:tier-change', onTierChange);

    // Cross-frame postMessage bridge — when the Shopify storefront page
    // (parent of this iframe) reads the logged-in customer's subscription
    // metafield and posts it to us, lift the tier accordingly.
    //
    // Expected message shape from parent:
    //   { type: 'spx-tier', tier: 'free' | 'hunter' | 'pro' }
    //
    // The parent decides tier based on:
    //   - Customer metafield (active subscription tag) — set by Subscriptions app webhook
    //     pro-tier → 'pro'
    //     hunter-tier OR competition-tier → 'hunter' (both grant same access)
    //     no active sub → 'free'
    let gotTier = false;
    const onMessage = (e: MessageEvent) => {
      // Origin check (hardened 2026-06-09): only the Sparrow storefront and
      // Shopify preview domains may set tier. Without this, any page that
      // iframes the app could postMessage itself Pro.
      if (!isTrustedTierOrigin(e.origin)) return;
      const d = e.data as { type?: string; tier?: string } | null;
      if (!d || d.type !== 'spx-tier') return;
      if (d.tier === 'free' || d.tier === 'hunter' || d.tier === 'pro') {
        gotTier = true;
        // ROOT-CAUSE FIX (2026-06-10): apply tier to state FIRST — the old
        // order wrote localStorage before setTier with no guard; in a
        // cross-origin iframe with third-party storage blocked the write
        // THROWS and the handler died before the tier applied.
        setTier(d.tier);
        try {
          (window as unknown as { SPARROW_TIER?: string }).SPARROW_TIER = d.tier;
        } catch { /* belt and suspenders */ }
        try {
          localStorage.setItem('spx_tier', d.tier);
        } catch { /* third-party storage blocked — state still applied */ }
      }
    };
    window.addEventListener('message', onMessage);

    // ACTIVE HANDSHAKE (2026-06-10): ask the parent for our tier every 1s
    // until answered — the embed pages' bridge answers spx-tier-request and
    // sparrowForge.identityRequest. Kills the mount race.
    let attempts = 0;
    const requestTier = () => {
      if (gotTier || attempts >= 30) return;
      attempts += 1;
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'sparrowForge.identityRequest', reqId: `tier-${attempts}` }, '*');
          window.parent.postMessage({ type: 'spx-tier-request' }, '*');
        }
      } catch { /* no parent */ }
    };
    requestTier();
    const handshake = window.setInterval(() => {
      if (gotTier || attempts >= 30) {
        window.clearInterval(handshake);
        return;
      }
      requestTier();
    }, 1000);

    return () => {
      window.removeEventListener('sparrow:tier-change', onTierChange);
      window.removeEventListener('message', onMessage);
      window.clearInterval(handshake);
    };
  }, []);

  const hasTier = (required: Tier) => TIER_RANK[tier] >= TIER_RANK[required];

  return {
    tier,
    isPro: tier === 'pro',
    isHunterOrAbove: hasTier('hunter'),
    hasTier,
  };
}

/**
 * Resolves the user's effective tier from (in priority order):
 *   1. window.SPARROW_TIER — set by build-time inliner (--tier=free|pro flag)
 *      OR by postMessage from the parent Shopify page (customer's real sub).
 *   2. localStorage 'spx_tier' — persisted choice from the tier-picker modal,
 *      so a user who picked "Hunter trial" still sees Hunter features after
 *      reload. Once their real subscription syncs via postMessage it'll
 *      overwrite this.
 *   3. URL ?tier=hunter param — for quick testing + deep-link from
 *      marketing pages.
 *   4. Default 'pro' — only in the bare standalone dev build with no inliner.
 */
/**
 * Origins allowed to set tier via postMessage. Exported for tests.
 *  - sparrowexpeditions.com (+ subdomains: www, forge)
 *  - *.myshopify.com (theme editor / preview)
 *  - localhost / 127.0.0.1 / *.pages.dev (dev + previews)
 */
export function isTrustedTierOrigin(origin: string): boolean {
  let host: string;
  try {
    host = new URL(origin).hostname;
  } catch {
    return false;
  }
  return (
    host === 'sparrowexpeditions.com' ||
    host.endsWith('.sparrowexpeditions.com') ||
    host.endsWith('.myshopify.com') ||
    host.endsWith('.pages.dev') ||
    host === 'localhost' ||
    host === '127.0.0.1'
  );
}

function readEffectiveTier(): Tier {
  if (typeof window === 'undefined') return 'pro';
  const t = (window as unknown as { SPARROW_TIER?: string }).SPARROW_TIER;
  if (t === 'free' || t === 'hunter' || t === 'pro') return t;

  // Dev/preview escape hatches (?tier= and localStorage) are honored ONLY on
  // dev hosts. On the public production domain anyone could grant themselves
  // Pro with ?tier=pro, so these paths are dead there (hardened 2026-06-09,
  // closes the second half of the tool-page gating leak).
  const host = window.location.hostname;
  const isDevHost =
    host === 'localhost' || host === '127.0.0.1' || host.endsWith('.pages.dev');
  if (isDevHost) {
    // URL param override — for testing + deep links from pricing page
    try {
      const url = new URL(window.location.href);
      const q = url.searchParams.get('tier');
      if (q === 'free' || q === 'hunter' || q === 'pro') return q;
    } catch { /* malformed URL — ignore */ }

    // Persisted choice from tier picker
    try {
      const stored = localStorage.getItem('spx_tier');
      if (stored === 'free' || stored === 'hunter' || stored === 'pro') return stored;
    } catch { /* localStorage blocked (private mode) — fall through */ }
  }

  // Default: gate everyone to FREE. Real subscribers are lifted by the Shopify
  // embed posting {type:'spx-tier'} (forge-tool-embed.liquid reads customer.tags)
  // or via ?tier= for testing. (Was 'pro', which leaked paid tools to everyone.)
  return 'free';
}

/**
 * Open the upgrade / pricing page.
 *
 * @param suggestedTier  Which tier the upsell should land the user on. If the
 *                       user is currently on free and clicks a Pro-only feature,
 *                       this lets the gate suggest the right plan ("Upgrade to
 *                       Pro for shot solver" rather than just "Upgrade").
 */
export function openUpgrade(suggestedTier?: Tier): void {
  if (typeof window === 'undefined') return;
  const params = suggestedTier && suggestedTier !== 'free' ? `?plan=${suggestedTier}` : '';
  // Use top-frame nav so the pricing page opens in the user's main browser
  // window even when the builder is iframe-embedded inside the storefront.
  const url = `https://sparrowexpeditions.com/pages/pricing${params}`;
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return;
    }
  } catch { /* cross-origin frame — fall through */ }
  window.open(url, '_blank', 'noopener');
}
